<script lang="ts" setup>
import { useField } from "vee-validate";
import BaseInput from "./_base.vue";
import Multiselect from "@vueform/multiselect";

interface Props {
    name: string;
    label?: string;
    placeholder: string;
    options: Array<{ value: string; label: string }>;
    inputClass?: string;
    mode: "single" | "tags";
}

const props = withDefaults(defineProps<Props>(), {
    name: undefined,
    label: undefined,
    placeholder: undefined,
    inputClass: "md",
    mode: "tags",
});

const { value, handleChange, handleBlur, errorMessage, meta } = useField(props.name);
const mode = typeof value.value === "string" ? "single" : props.mode;
const delayedBlur = () => setTimeout(handleBlur, 500);

defineExpose({ value });
</script>

<template>
    <BaseInput :errorMessage="errorMessage" :meta="meta" :name="name" :label="label">
        <Multiselect
            :class="[inputClass, { 'relative z-[12] mt-2': label && label !== '' }]"
            v-model="value"
            @change="handleChange"
            @select="$emit('select')"
            @deselect="$emit('select')"
            @clear="$emit('select')"
            @close="delayedBlur"
            :name="name"
            :mode="mode"
            :hide-selected="false"
            :close-on-select="false"
            :searchable="false"
            valueProp="value"
            trackBy="name"
            label="label"
            :placeholder="placeholder"
            :options="options"
        />
    </BaseInput>
</template>

<style lang="scss" scoped>
:deep(.multiselect-tags-search-wrapper) {
    margin: 0;
}
:deep(.multiselect-tags) {
    overflow: hidden;
}
:deep(.multiselect-tag:not(:last-child):not(:nth-last-child(2)):after) {
    content: ",";
}
:deep(.multiselect-tag-remove) {
    display: none;
}
:deep(.multiselect-option) {
    padding-left: 52px;
    @apply relative before:absolute before:left-4 before:top-1/2 before:h-5 before:w-5 before:-translate-y-1/2 before:rounded before:border before:border-gray-500 before:transition-colors after:absolute after:left-5 after:top-1/2 after:h-3 after:w-[.625rem] after:translate-y-[calc(-50%+2px)] after:bg-[url('svg/check.svg')] after:bg-no-repeat after:opacity-0 after:transition-opacity;

    &:hover {
        @apply before:border-orange;
    }

    &.is-selected {
        @apply font-bold before:border-orange after:opacity-100;
    }
}
</style>
